import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Divider,
  Heading,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  VStack,
} from '@chakra-ui/react';
import { faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import SparkelIcon from '../common/icon/SparkelIcon';
import { AutoShapeAlgorithmType, AutoShapeDeepFragment } from 'src/gql/graphql';
import {
  AlgorithmSettingsProps,
  algorithmSettingsRegistry,
  AutoShapeAlgorithmOptionsMap,
} from 'src/components/auto-shapes/types';
import { getAlgorithmMetadata, useAutoShapes } from 'src/hooks/auto-shapes';

type Props = {
  autoShape: AutoShapeDeepFragment;
  trigger: React.ReactNode;
  parentIsOpen?: boolean;
};

export function EditAutoshapePopover({
  autoShape,
  trigger,
  parentIsOpen,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);

  // Local editable options copied from the autoshape
  const initialOptions = useMemo(() => {
    return (autoShape.algorithmInput ?? {}) as Record<string, unknown>;
  }, [autoShape.algorithmInput]);

  const [options, setOptions] =
    useState<Record<string, unknown>>(initialOptions);

  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions, isOpen]);

  // Close popover when parent menu closes
  useEffect(() => {
    if (parentIsOpen === false) {
      setIsOpen(false);
    }
  }, [parentIsOpen]);

  const { updateAutoShape } = useAutoShapes();

  const SettingsComponent = useMemo(() => {
    return algorithmSettingsRegistry[
      autoShape.algorithmType as AutoShapeAlgorithmType
    ] as React.ComponentType<AlgorithmSettingsProps<AutoShapeAlgorithmType>>;
  }, [autoShape.algorithmType]);

  const onRun = async () => {
    await updateAutoShape(
      autoShape.id,
      autoShape.algorithmType as AutoShapeAlgorithmType,
      options as unknown as AutoShapeAlgorithmOptionsMap[AutoShapeAlgorithmType]
    );

    setIsOpen(false);
  };

  const algoMeta = getAlgorithmMetadata(
    autoShape.algorithmType as AutoShapeAlgorithmType
  );

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="auto"
      closeOnBlur={false}
      returnFocusOnClose={false}
    >
      <PopoverTrigger>
        <span onClick={() => setIsOpen(true)}>{trigger}</span>
      </PopoverTrigger>
      <PopoverContent
        p={2}
        maxW="320px"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverBody>
          <VStack align="stretch" spacing={2}>
            <Heading size="sm">{algoMeta.name}</Heading>
            {SettingsComponent && (
              <SettingsComponent
                value={
                  options as unknown as AutoShapeAlgorithmOptionsMap[AutoShapeAlgorithmType]
                }
                onChange={(val) =>
                  setOptions(val as unknown as Record<string, unknown>)
                }
                onValidityChange={setIsValid}
              />
            )}
            <Divider />
            <Button
              isDisabled={!isValid}
              size="xs"
              leftIcon={<SparkelIcon icon={faPlayCircle} color={'inherit'} />}
              onClick={onRun}
              marginLeft="auto"
              borderRadius="md"
              variant="outline"
            >
              Run
            </Button>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
